<template>
  <div class="w-full flex flex-col justify-center px-6 py-2 relative">
    <!-- Language -->
    <div
      :class="[
        'absolute top-4 z-30 flex gap-2',
        locale === 'ar' ? 'right-4 flex-row-reverse' : 'left-4 flex-row',
      ]"
    >
      <img
        src="@/assets/icons/language.svg"
        alt="Language"
        class="w-6 h-6 cursor-pointer"
        @click="toggleLang"
      />
    </div>

    <p
      class="text-[27px] md:text-[22px] lg:text-[27px] font-semibold font-[Inter] text-[var(--primary)] text-center mb-2 mt-2"
    >
      {{ $t("signup.signupForm.title") }}
    </p>

    <form @submit.prevent="signup" class="space-y-4 mt-2">
      <div class="text-center mb-4">
        <label
          class="block text-[var(--on-surface)] text-[18px] font-[Inter] mb-1"
        >
          {{ $t("signup.signupForm.detailsLabel") }}
        </label>
      </div>

      <!-- Username -->
      <div
        class="bg-[var(--surface-2)] px-3 py-2 rounded relative flex items-center"
      >
        <template v-if="locale === 'ar'">
          <input
            ref="usernameInput"
            v-model="username"
            type="text"
            :placeholder="$t('signup.signupForm.username')"
            class="w-full bg-transparent text-[var(--on-surface)] placeholder-[var(--muted)] focus:outline-none text-right pr-10"
            required
            dir="rtl"
            @keydown.enter="focusNext('email')"
          />
          <img
            src="@/assets/icons/user.svg"
            alt="Username"
            class="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2"
          />
        </template>
        <template v-else>
          <input
            ref="usernameInput"
            v-model="username"
            type="text"
            :placeholder="$t('signup.signupForm.username')"
            class="w-full bg-transparent text-[var(--on-surface)] placeholder-[var(--muted)] focus:outline-none text-left pl-10"
            required
            dir="ltr"
            @keydown.enter="focusNext('email')"
          />
          <img
            src="@/assets/icons/user.svg"
            alt="Username"
            class="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2"
          />
        </template>
      </div>

      <!-- Email -->
      <div
        class="bg-[var(--surface-2)] px-3 py-2 rounded relative flex items-center"
      >
        <template v-if="locale === 'ar'">
          <input
            ref="emailInput"
            v-model="email"
            type="email"
            :placeholder="$t('signup.signupForm.email')"
            class="w-full bg-transparent text-[var(--on-surface)] placeholder-[var(--muted)] focus:outline-none text-right pr-10"
            required
            dir="rtl"
            @keydown.enter="focusNext('phone')"
          />
          <img
            src="@/assets/icons/email.svg"
            alt="Email"
            class="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2"
          />
        </template>
        <template v-else>
          <input
            ref="emailInput"
            v-model="email"
            type="email"
            :placeholder="$t('signup.signupForm.email')"
            class="w-full bg-transparent text-[var(--on-surface)] placeholder-[var(--muted)] focus:outline-none text-left pl-10"
            required
            dir="ltr"
            @keydown.enter="focusNext('phone')"
          />
          <img
            src="@/assets/icons/email.svg"
            alt="Email"
            class="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2"
          />
        </template>
      </div>

      <!-- Phone -->
      <div
        class="bg-[var(--surface-2)] px-3 py-2 rounded relative flex items-center"
      >
        <template v-if="locale === 'ar'">
          <input
            ref="phoneInput"
            v-model="phone"
            type="tel"
            :placeholder="$t('signup.signupForm.phone')"
            class="w-full bg-transparent text-[var(--on-surface)] placeholder-[var(--muted)] focus:outline-none text-right pr-10"
            required
            maxlength="11"
            dir="rtl"
            @input="validatePhone"
            @keydown.enter="focusNext('password')"
          />
          <img
            src="@/assets/icons/phone.svg"
            alt="Phone"
            class="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2"
          />
        </template>
        <template v-else>
          <input
            ref="phoneInput"
            v-model="phone"
            type="tel"
            :placeholder="$t('signup.signupForm.phone')"
            class="w-full bg-transparent text-[var(--on-surface)] placeholder-[var(--muted)] focus:outline-none text-left pl-10"
            required
            maxlength="11"
            dir="ltr"
            @input="validatePhone"
            @keydown.enter="focusNext('password')"
          />
          <img
            src="@/assets/icons/phone.svg"
            alt="Phone"
            class="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2"
          />
        </template>
      </div>
      <div v-if="phoneError" class="text-[var(--danger)] text-xs px-3">
        {{ phoneError }}
      </div>

      <!-- Password -->
      <div
        class="bg-[var(--surface-2)] px-3 py-2 rounded relative flex items-center"
      >
        <template v-if="locale === 'ar'">
          <input
            ref="passwordInput"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            :placeholder="$t('signup.signupForm.password')"
            class="w-full bg-transparent text-[var(--on-surface)] placeholder-[var(--muted)] focus:outline-none text-right pr-10"
            required
            dir="rtl"
            @keydown.enter="handlePasswordEnter"
          />
          <img
            src="@/assets/icons/password.svg"
            alt="Password"
            class="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2"
          />
        </template>
        <template v-else>
          <input
            ref="passwordInput"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            :placeholder="$t('signup.signupForm.password')"
            class="w-full bg-transparent text-[var(--on-surface)] placeholder-[var(--muted)] focus:outline-none text-left pl-10"
            required
            dir="ltr"
            @keydown.enter="handlePasswordEnter"
          />
          <img
            src="@/assets/icons/password.svg"
            alt="Password"
            class="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2"
          />
        </template>
      </div>

      <!-- Agree to Terms -->
      <div
        :class="['flex items-start gap-2 py-1', 'flex-row']"
        id="theStatement"
      >
        <button
          type="button"
          @click="toggleAgree"
          @keydown.enter="handleAgreeEnter"
          class="focus:outline-none flex items-center justify-center mt-0.5"
        >
          <span
      :class="[
        'relative w-4 h-4 rounded grid place-items-center transition',
        agree
          ? 'bg-[var(--primary)] border border-[var(--primary)] shadow-[var(--checkbox-glow-on)]'
          : 'bg-[var(--checkbox-bg-off)] border border-[var(--checkbox-border)]'
      ]"
    >
            <img
              :src="agree ? checkAgreeIcon : checkIcon"
              alt="Agree"
              class="w-3 h-3"
            />
          </span>
        </button>
        <label
          :class="[
            'text-[var(--on-surface)] text-sm font-normal',
            locale === 'ar' ? 'text-right' : 'text-left',
          ]"
          style="font-family: 'DM Sans', sans-serif"
        >
          {{ $t("signup.signupForm.agree") }}
        </label>
      </div>

      <!-- Submit Button (primary glow like login) -->
      <button
        type="submit"
        class="w-full bg-[var(--login-btn-bg)] text-[var(--login-btn-text)] font-semibold py-2 rounded-none text-sm transition-all duration-300 hover:shadow-[var(--glow-login)] hover:[filter:var(--login-drop-glow)]"
        :disabled="!agree"
        @keydown.enter="handleSubmitEnter"
      >
        {{ $t("signup.signupForm.submit") }}
      </button>
    </form>

    <!-- Error Message -->
    <div
      v-if="errorMessage"
      class="mt-4 p-3 bg-[color-mix(in_srgb,var(--danger)_20%,transparent)] border border-[color-mix(in_srgb,var(--danger)_30%,transparent)] rounded-lg text-center"
    >
      <div class="flex items-center justify-center gap-2">
        <svg
          class="w-4 h-4"
          :style="{ color: 'var(--danger)' }"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"
          />
        </svg>
        <span class="text-sm font-medium text-[var(--danger)]">{{
          errorMessage
        }}</span>
      </div>
    </div>

    <!-- Google button (dark: white bg + white glow, light: white bg + border + blueish glow per your preference) -->
    <div class="flex justify-center mb-4 mt-6">
      <button
        @click="signupWithGoogle"
        class="w-full flex items-center justify-center gap-2 py-2 border bg-[var(--google-btn-bg)] border-[var(--google-btn-border,transparent)] text-[var(--google-btn-text)] transition-all duration-300 focus:outline-none hover:shadow-[var(--glow-google)] hover:[filter:var(--google-drop-glow)]"
      >
        <img src="@/assets/icons/google.svg" alt="Google" class="w-5 h-5" />
        <span class="text-sm font-medium">Google</span>
      </button>
    </div>

    <!-- Login redirect -->
    <div class="text-center text-[var(--muted)]">
      {{ $t("signup.signupForm.haveAccount") }}
      <RouterLink
        to="/login"
        class="text-[var(--primary)] font-semibold hover:underline"
      >
        {{ $t("signup.signupForm.login") }}
      </RouterLink>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import checkIcon from "@/assets/icons/check.svg";
import checkAgreeIcon from "@/assets/icons/check-agree.svg";

const router = useRouter();
const { locale } = useI18n();

function toggleLang() {
  locale.value = locale.value === "en" ? "ar" : "en";
  localStorage.setItem("lang", locale.value);
  document.documentElement.setAttribute(
    "dir",
    locale.value === "ar" ? "rtl" : "ltr"
  );
}

const username = ref("");
const email = ref("");
const phone = ref("");
const password = ref("");
const agree = ref(false);
const errorMessage = ref("");
const phoneError = ref("");
const showPassword = ref(false);

// Refs
const usernameInput = ref(null);
const emailInput = ref(null);
const phoneInput = ref(null);
const passwordInput = ref(null);

const toggleAgree = () => {
  agree.value = !agree.value;
};

const focusNext = (fieldName) => {
  if (fieldName === "email") emailInput.value?.focus();
  if (fieldName === "phone") phoneInput.value?.focus();
  if (fieldName === "password") passwordInput.value?.focus();
};

const handlePasswordEnter = () => {
  if (agree.value) signup();
  else
    document.getElementById("theStatement")?.querySelector("button")?.focus();
};

const handleAgreeEnter = () => {
  toggleAgree();
  if (agree.value) signup();
};
const handleSubmitEnter = () => {
  if (agree.value) signup();
};

const validatePhone = () => {
  const phoneNumber = phone.value.replace(/\D/g, "");
  if (!phoneNumber) {
    phoneError.value = "Phone number is required";
    return false;
  }
  if (phoneNumber.length !== 11) {
    phoneError.value = "Phone number must be exactly 11 digits";
    return false;
  }
  if (!phoneNumber.startsWith("01")) {
    phoneError.value =
      "Please enter a valid Egyptian mobile number (starts with 01)";
    return false;
  }
  phoneError.value = "";
  return true;
};

import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "@/firebase";
import { doc, setDoc } from "firebase/firestore";
import { db } from "@/firebase";

const signup = async () => {
  if (!agree.value) return;
  errorMessage.value = "";

  if (!username.value.trim()) {
    errorMessage.value = "Please enter a username!";
    return;
  }
  if (!email.value.trim()) {
    errorMessage.value = "Please enter your email address!";
    return;
  }
  if (!phone.value.trim()) {
    errorMessage.value = "Please enter your phone number!";
    return;
  }
  if (!validatePhone()) return;
  if (!password.value) {
    errorMessage.value = "Please enter a password!";
    return;
  }
  if (password.value.length < 6) {
    errorMessage.value = "Password must be at least 6 characters long!";
    return;
  }

  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email.value,
      password.value
    );
    const user = userCredential.user;

    await setDoc(doc(db, "users", user.uid), {
      username: username.value,
      phone: phone.value,
      email: email.value,
      createdAt: new Date(),
    });

    await sendEmailVerification(user);
    router.push("/verifyEmail");
  } catch (error) {
    switch (error.code) {
      case "auth/email-already-in-use":
        errorMessage.value =
          "This email is already registered. Try logging in instead!";
        break;
      case "auth/invalid-email":
        errorMessage.value =
          "That doesn't look like a valid email. Please check the format!";
        break;
      case "auth/weak-password":
        errorMessage.value =
          "Password is too weak. Choose a stronger password!";
        break;
      case "auth/operation-not-allowed":
        errorMessage.value =
          "Email/password accounts are not enabled. Please contact support.";
        break;
      case "auth/too-many-requests":
        errorMessage.value =
          "Too many attempts! Take a breather and try again in a moment.";
        break;
      default:
        errorMessage.value =
          "Something went wrong during signup. Please try again!";
    }
  }
};

async function signupWithGoogle() {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    await setDoc(doc(db, "users", user.uid), {
      username: user.displayName || user.email?.split("@")[0] || "User",
      phone: user.phoneNumber || "",
      email: user.email,
      createdAt: new Date(),
    });

    if (!user.emailVerified) await user.sendEmailVerification();
    router.push("/bookingduration");
  } catch (error) {
    console.error("Google signup error:", error.message);
    errorMessage.value = "Google signup failed. Please try again.";
  }
}
</script>

<style scoped>
/* Autofill themed */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px var(--surface-2) inset !important;
  -webkit-text-fill-color: var(--on-surface) !important;
  transition: background-color 5000s ease-in-out 0s;
}
input:-moz-autofill {
  background-color: var(--surface-2) !important;
  color: var(--on-surface) !important;
}
input:-ms-input-placeholder {
  color: var(--muted) !important;
}
input::-webkit-calendar-picker-indicator {
  display: none !important;
}
input[autocomplete] {
  background-color: transparent !important;
}
input:focus {
  background-color: transparent !important;
  outline: none !important;
}
input:not(:focus) {
  background-color: transparent !important;
}
</style>
